import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminPanel from './AdminPanel';
import './style1.css';

interface DeleteCertificateProps {
    username: string | null;
    onLogout?: () => void;
}

const navLinks = [
    { name: 'HOME', href: '/' },
    { name: 'PROGRAMS', href: '/programs' },
    { name: 'VIDEOS', href: '/videos' },
    { name: 'IMAGES', href: '/images' },
    { name: 'DEMOS', href: '/demos' },
    { name: 'CERTIFICATES', href: '/certificates' },
    { name: 'ABOUT', href: '/about' },
];

const DeleteCertificate: React.FC<DeleteCertificateProps> = ({ username, onLogout }) => {
    const [certificateId, setCertificateId] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [confirmAll, setConfirmAll] = useState(false);
    const navigate = useNavigate();

    // Only admin can access
    if (username !== 'admin') {
        return <div style={{ textAlign: 'center', marginTop: '2rem' }}>Access denied.</div>;
    }

    const handleDeleteCertificate = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!certificateId) {
            setError('Certificate ID is required.');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/deletecertificate/${certificateId}`, {
                method: 'DELETE',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            if (response.ok) {
                setSuccess('Certificate deleted!');
                setCertificateId('');
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to delete certificate.');
            }
        } catch (err) {
            setError('Network error.');
        }
    };

    const handleDeleteAllCertificates = async () => {
        setError('');
        setSuccess('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/deleteallcertificates', {
                method: 'DELETE',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            if (response.ok) {
                setSuccess('All certificates deleted!');
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to delete all certificates.');
            }
        } catch (err) {
            setError('Network error.');
        }
        setConfirmAll(false);
    };

    return (
        <div className="modern-layout" style={{ position: 'relative' }}>
            <header className="modern-header">
                <div className="header-container">
                    <nav className="modern-nav">
                        {navLinks.map(link =>
                            link.href.startsWith('/')
                                ? <Link
                                    key={link.name}
                                    to={link.href}
                                    className="modern-nav-link"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {link.name}
                                  </Link>
                                : <a
                                    key={link.name}
                                    href={link.href}
                                    className="modern-nav-link"
                                  >
                                    {link.name}
                                  </a>
                        )}
                        {onLogout && (
                            <button className="logout-btn" onClick={onLogout}>LOG OUT</button>
                        )}
                    </nav>
                </div>
            </header>
            <div
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 120,
                    zIndex: 10
                }}
            >
                <AdminPanel />
            </div>
            <main className="modern-content">
                <div
                    style={{
                        maxWidth: 400,
                        margin: '2rem auto',
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 2px 16px #6a11cb22',
                        padding: '2rem'
                    }}
                >
                    <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Delete Certificate</h2>
                    <form onSubmit={handleDeleteCertificate}>
                        <div style={{ marginBottom: '1rem' }}>
                            <label>Certificate ID:</label>
                            <input
                                type="text"
                                value={certificateId}
                                onChange={e => setCertificateId(e.target.value)}
                                style={{ width: '100%', padding: '0.5rem', marginTop: 4 }}
                            />
                        </div>
                        <button
                            type="submit"
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: 'linear-gradient(135deg, #cb116a 0%, #fc2525 100%)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                marginBottom: '1rem'
                            }}
                        >
                            Delete Certificate
                        </button>
                    </form>
                    <hr style={{ margin: '2rem 0' }} />
                    <button
                        onClick={() => setConfirmAll(true)}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'linear-gradient(135deg, #cb116a 0%, #fc2525 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            fontWeight: 'bold',
                            cursor: 'pointer'
                        }}
                    >
                        Delete All Certificates
                    </button>
                    {confirmAll && (
                        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                            <div style={{ color: 'red', marginBottom: '1rem' }}>
                                Are you sure you want to delete <b>ALL</b> certificates? This cannot be undone!
                            </div>
                            <button
                                onClick={handleDeleteAllCertificates}
                                style={{
                                    padding: '0.5rem 1.5rem',
                                    background: '#d32f2f',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    fontWeight: 'bold',
                                    marginRight: '1rem',
                                    cursor: 'pointer'
                                }}
                            >
                                Yes, Delete All
                            </button>
                            <button
                                onClick={() => setConfirmAll(false)}
                                style={{
                                    padding: '0.5rem 1.5rem',
                                    background: '#888',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                    {error && <div style={{ color: 'red', marginTop: '1rem' }}>{error}</div>}
                    {success && <div style={{ color: 'green', marginTop: '1rem' }}>{success}</div>}
                </div>
            </main>
            <footer className="modern-footer">
                &copy; {new Date().getFullYear()} Timo
            </footer>
        </div>
    );
};

export default DeleteCertificate;